import React, { RefObject, useCallback, useEffect, useState } from "react";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { categoriesGql, getAffariyets } from "gql/queries";
import { useQuery } from "@apollo/client";

import {
  AffariyetEntity,
  AffariyetEntityResponseCollection,
  CategoryEntity,
  CategoryEntityResponseCollection,
  SubCategoryEntity,
  SubCategoryEntityResponseCollection,
} from "gql-types";

// import { getSubCategories } from "gql/queries/categories.gql";
import { useDebounce } from "hooks/useDebounce";
import FilterOptions from "./FilterOptions";
import NoAffariyetPage from "./NoAffariyetPage";
import Pagination from "../../components/pagination/Pagination";
import Affariyet from "./Affariyet";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { routes } from "helpers/routes";
import { offerCardType } from "helpers/types";
import { subCategoriesNameListByCategoryNameGql } from "gql/queries/subCategoriesNameListByCategoryName.gql";
import { h1FontSize, h2FontSize, mobileScreen } from "utils/common";
import Head from "next/head";
import SeoHead from "components/ui_components/SeoHead";
import { IS_PRODUCTION_ENVIRONMENT } from "helpers/constants";

import dynamic from "next/dynamic";
import "react-loading-skeleton/dist/skeleton.css";
import moment from "moment";

const Skeleton = dynamic(() => import("react-loading-skeleton"), {
  ssr: false,
});

const Offers = ({
  offersRef,
  storeId,
  cardType = offerCardType.OFFERS,
  fromHomePage,
  isProduction,
  fromStore,
}: {
  offersRef: RefObject<HTMLDivElement>;
  storeId?: number;
  cardType: offerCardType;
  fromHomePage?: boolean;
  isProduction?: boolean;
  fromStore?: boolean;
}) => {
  const { t: translate } = useTranslation("offers");

  const DEFAULT_CATEGORY = translate("categories", { lng: "fr" });
  const DEFAULT_SUB_CATEGORY = translate("sub-categories", { lng: "fr" });
  const pathName = usePathname();
  const headingText = {
    [offerCardType.GENERIC]: "find_affar_in_affariyet",
    [offerCardType.STORE_SPECIFIC]: "affariyet_of_boutique",
    [offerCardType.OFFERS]: "offers",
  };
  const [searchQuery, setSearchQuery] = useState<string>("");
  const debouncedSearchQuery = useDebounce<string>(searchQuery);
  const [affariyets, setAffariyets] = useState<AffariyetEntity[]>([]);
  const [categories, setCategories] = useState<CategoryEntity[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>(DEFAULT_CATEGORY);
  const [subCategories, setSubCategories] = useState<SubCategoryEntity[]>([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>(DEFAULT_SUB_CATEGORY);
  const [affariyetOfToday, setAffariyetOfToday] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pagesNumber, setPagesNumber] = useState<number>(1);
  const [affariyetItemsPerPage, setAffariyetItemsPerPage] = useState<number>(
    typeof window !== "undefined" && window.innerWidth > 550 ? 12 : 4,
  );

  useEffect(() => {
    const updateItemsPerPage = () => {
      if (typeof window !== "undefined") {
        setAffariyetItemsPerPage(window.innerWidth > 550 ? 12 : 4);
      }
    };

    updateItemsPerPage();
    window.addEventListener("resize", updateItemsPerPage);
    return () => {
      window.removeEventListener("resize", updateItemsPerPage);
    };
  }, [affariyetItemsPerPage]);
  const getFilterVariables = useCallback(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);
    const todayDate = moment().format("YYYY-MM-DD");
    const filters: any = {
      expires_at: { gte: todayDate },
      store: {
        id: storeId ? { eq: storeId } : { not: null },
        ...(debouncedSearchQuery ? { name: { containsi: debouncedSearchQuery } } : {}),
        ...(selectedSubCategory !== DEFAULT_SUB_CATEGORY || selectedCategory !== DEFAULT_CATEGORY
          ? {
              sub_category: {
                ...(selectedSubCategory !== DEFAULT_SUB_CATEGORY && selectedSubCategory
                  ? { name: { eq: selectedSubCategory } }
                  : {}),
                ...(selectedCategory !== DEFAULT_CATEGORY && selectedCategory
                  ? { category: { name: { eq: selectedCategory } } }
                  : {}),
              },
            }
          : {}),
      },
    };
    if (affariyetOfToday) {
      filters.createdAt = {
        gte: todayStart.toISOString(),
        lte: todayEnd.toISOString(),
      };
    }

    return {
      filters,
      pagination: { page, pageSize: affariyetItemsPerPage },
    };
  }, [
    storeId,
    selectedSubCategory,
    selectedCategory,
    debouncedSearchQuery,
    affariyetOfToday,
    page,
    affariyetItemsPerPage,
  ]);

  useEffect(() => {
    setPage(1);
  }, [selectedCategory, selectedSubCategory, debouncedSearchQuery, affariyetOfToday]);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const { refetch, loading } = useQuery<{
    affariyets?: AffariyetEntityResponseCollection;
  }>(getAffariyets, {
    variables: getFilterVariables(),
    onCompleted: (data) => {
      setPagesNumber((data.affariyets?.meta.pagination.total as number) / affariyetItemsPerPage);
      setAffariyets(data?.affariyets?.data ?? []);
    },
    onError: (error) => {
      console.error("error", error);
    },
  });

  // useEffect(() => {
  //   refetch({ limit: affariyetItemsPerPage });
  // }, [affariyetItemsPerPage, refetch]);
  //get categories
  const {} = useQuery<{ categories: CategoryEntityResponseCollection }>(categoriesGql, {
    onCompleted: (data) => {
      setCategories(data.categories.data);
    },
  });

  // const {} = useQuery<{ subCategories: SubCategoryEntityResponseCollection }>(getSubCategories, {

  //   onCompleted: (data) => {
  //     setSubCategories(data.subCategories.data);
  //   },
  // });

  const formatSlug = (name: string) =>
    name
      .toLowerCase()
      .replace(/\s+/g, "-")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  useQuery<{ subCategories?: SubCategoryEntityResponseCollection }>(
    subCategoriesNameListByCategoryNameGql,
    {
      variables: { catName: formatSlug(selectedCategory) },
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        setSubCategories(data?.subCategories?.data ?? []);
      },
    },
  );

  //filter affariyet items
  useEffect(() => {
    // window.scrollTo({
    //   top: 100,
    //   behavior: "smooth",
    // });
    if (storeId) {
      //check if there is store id no need to refetch
      return;
    }
    refetch({
      variables: getFilterVariables(),
      limit: affariyetItemsPerPage,
    });
  }, [getFilterVariables, refetch, affariyetItemsPerPage, storeId]);

  const handleSetPage = (newPage) => {
    setPage(newPage);

    if (window.innerWidth <= mobileScreen) {
      window.scrollTo({ top: 200, behavior: "smooth" });
    }
  };
  const OffersSection = (
    <div className=" bg-backgroundGrey p-3 py-5 " id="offers">
      {!fromHomePage && !fromStore && (
        <SeoHead
          title={translate("offers_title")}
          description={translate("offers_description")}
          isProduction={isProduction}
        />
      )}

      <section
        ref={offersRef}
        className={`bg-white rounded-[21px] ${
          pathName === routes.OFFERS_PATH && "min-h-screen"
        }  px-6 lg:px-40 py-6 flex flex-col items-center gap-6 flex-1`}
      >
        {cardType === offerCardType.STORE_SPECIFIC || fromHomePage ? (
          <h2
            className={`font-bold ${h2FontSize} leading-7 text-blue rtl:text-right ltr:text-left`}
          >
            {cardType === offerCardType.STORE_SPECIFIC ? (
              translate(headingText[cardType])
            ) : (
              <React.Fragment>
                {translate("find_affar_in_affariyet_label")}{" "}
                <span className="relative z-10 after:absolute after:right-0 after:bottom-0 after:w-full after:h-1/4 after:bg-yellow after:-z-10">
                  {translate("find_affar_in_affariyet_highlight")}
                </span>
              </React.Fragment>
            )}
          </h2>
        ) : (
          <h1
            className={`font-bold ${h1FontSize} leading-7 text-blue`}
            dangerouslySetInnerHTML={{
              __html: translate(headingText[cardType ?? offerCardType.OFFERS]),
            }}
          />
        )}

        {pathName !== routes.OFFERS_PATH || (
          <>
            <p className="font-normal  text-center text-sm lg:text-base  text-black/50 leading-6 max-w-[700px]">
              {translate("description")}
            </p>
            {/* Filter */}
            <FilterOptions
              setSearchQuery={setSearchQuery}
              searchQuery={searchQuery}
              setAffariyetOfToday={setAffariyetOfToday}
              setSelectedCategory={setSelectedCategory}
              setSelectedSubCategory={setSelectedSubCategory}
              categories={categories ?? []}
              subCategories={subCategories ?? []}
            />
          </>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-8 mt-20">
            {Array.from({ length: affariyetItemsPerPage || 6 }).map((_, index) => (
              <Skeleton
                key={`__index__affar__card__${index}`}
                containerClassName="w-64"
                className="h-80"
              />
            ))}
          </div>
        ) : affariyets?.length ? (
          <React.Fragment>
            {/* Pagination */}
            {pathName !== routes.HOME_PATH && pagesNumber > 1 && (
              <Pagination pagesNumber={pagesNumber} page={page} setPage={setPage} />
            )}

            {/* Affariyet */}
            <Affariyet
              affariyet={
                pathName === routes?.HOME_PATH ? affariyets?.slice(0, 3) : (affariyets ?? [])
              }
              fromStore={fromStore}
            />
            {pathName === routes.HOME_PATH && (
              <Link href={routes.OFFERS_PATH} passHref>
                <button
                  id="see_more_affariyet_btn"
                  type="button"
                  className="shadow bg-blue  rounded-md lg:rounded-xl text-white font-bold leading-3 lg:leading-6 text-[10px] lg:text-xl px-7 lg:px-8 py-3 lg:py-4"
                >
                  {translate("see_more")}
                </button>
              </Link>
            )}
            {/* Pagination */}
            {pathName !== routes.HOME_PATH && pagesNumber > 1 && (
              <Pagination pagesNumber={pagesNumber} page={page} setPage={handleSetPage} />
            )}
          </React.Fragment>
        ) : (
          <NoAffariyetPage />
        )}
      </section>
    </div>
  );

  return (
    <>
      {pathName !== routes.OFFERS_PATH
        ? affariyets?.length > 0
          ? OffersSection
          : null
        : OffersSection}
    </>
  );
};

export default Offers;

export async function getStaticProps({ locale }) {
  return {
    props: {
      isProduction: IS_PRODUCTION_ENVIRONMENT,
      ...(await serverSideTranslations(locale, [
        "layout",
        "offers",
        "categories",
        "signup_promotion",
      ])),
    },
  };
}
